import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Votre demande a été enregistrée avec succès! Nous vous contacterons bientôt.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-light text-gray-900 mb-6">
            Contactez <span className="font-semibold text-gold-400">-nous</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-gold-300 to-gold-400 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 font-light">Laissez-nous vos coordonnées, nous revenons vers vous rapidement</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <Card className="p-8 border-none shadow-xl bg-gradient-to-br from-charcoal to-charcoal-light text-white">
            <h3 className="text-2xl font-semibold mb-6">Informations de Contact</h3>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gold-300 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <div className="text-sm text-gray-300 mb-1">Email</div>
                <a href="mailto:flyoutbysam@gmail.com" data-testid="contact-email" className="text-lg hover:text-gold-300 transition-colors">
                  flyoutbysam@gmail.com
                </a>
              </div>
            </div>
          </Card>

          {/* Contact Form */}
          <Card className="p-8 border-none shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-gray-700 font-medium">Nom complet *</Label>
                <Input 
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="mt-2 border-gray-300 focus:border-gold-300 focus:ring-gold-300"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">Email *</Label>
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="mt-2 border-gray-300 focus:border-gold-300 focus:ring-gold-300"
                  placeholder="votre.email@exemple.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700 font-medium">Téléphone</Label>
                <Input 
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="contact-phone-input"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="mt-2 border-gray-300 focus:border-gold-300 focus:ring-gold-300"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-700 font-medium">Message</Label>
                <Textarea 
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  rows={4}
                  className="mt-2 border-gray-300 focus:border-gold-300 focus:ring-gold-300"
                  placeholder="Questions ou informations complémentaires..."
                />
              </div>

              <Button 
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-charcoal py-6 text-lg font-medium rounded-full transition-all duration-300 hover:scale-105"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
